import { useEffect, useState } from 'react';
import Box from 'elements/Box';
import { Button, Chip, Rating, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useSnackbar } from 'notistack';
import { ApiRequest } from 'GlobalFunctions';
import AlertDialog from 'elements/AlertDialog';
import StarRateRoundedIcon from '@mui/icons-material/StarRateRounded';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

export default function RatingsList(props: any) {
  const { setLoading } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [list, setList] = useState<any[]>([]);
  const [tab, setTab] = useState(0); // 0 = pendientes, 1 = aprobadas
  const [openAlert, setOpenAlert] = useState(false);
  const [activeItem, setActiveItem] = useState<any>(null);

  useEffect(() => {
    setLoading(false);
    loadList();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadList = async () => {
    await ApiRequest({
      url: '/ratings/list',
      method: 'GET',
      setResponse: (response: any) => {
        setList(response.list || []);
      }
    });
  };

  const handleApprove = async (rating_id: number) => {
    await ApiRequest({
      url: '/ratings/approve',
      method: 'POST',
      body: { rating_id },
      setResponse: () => {
        enqueueSnackbar('Reseña aprobada — ya aparece en el perfil', { variant: 'success' });
        loadList();
      }
    });
  };

  const handleReject = async (rating_id: number) => {
    await ApiRequest({
      url: '/ratings/reject',
      method: 'POST',
      body: { rating_id },
      setResponse: () => {
        enqueueSnackbar('Reseña desaprobada', { variant: 'warning' });
        loadList();
      }
    });
  };

  const handleDelete = (rating_id: number) => {
    setActiveItem(rating_id);
    setOpenAlert(true);
  };

  const deleteItem = async () => {
    await ApiRequest({
      url: '/ratings/delete',
      method: 'POST',
      body: { rating_id: activeItem },
      setResponse: () => {
        enqueueSnackbar('Reseña eliminada', { variant: 'success' });
        setOpenAlert(false);
        loadList();
      }
    });
  };

  const pending  = list.filter(r => !r.approved);
  const approved = list.filter(r =>  r.approved);
  const displayed = tab === 0 ? pending : approved;

  return (
    <div className="content">
      <div className="ModuleWrapper">
        <div className="boxContainerHeader">
          <div className="boxContainerTitle">
            <h2 className="h2_title">Reseñas</h2>
            <span className="h2_subtitle">Moderación de reseñas de enfermeras</span>
          </div>
        </div>

        {/* Summary chips */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <Chip
            label={`${pending.length} pendientes de aprobación`}
            color={pending.length > 0 ? 'warning' : 'default'}
            variant={pending.length > 0 ? 'filled' : 'outlined'}
          />
          <Chip
            label={`${approved.length} aprobadas`}
            color="success"
            variant="outlined"
          />
        </div>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label={`Pendientes (${pending.length})`} />
          <Tab label={`Aprobadas (${approved.length})`} />
        </Tabs>

        <Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: '#a8a8a8', borderBottom: 'none', fontWeight: 600 }}>Enfermera</TableCell>
                  <TableCell sx={{ color: '#a8a8a8', borderBottom: 'none', fontWeight: 600 }}>Paciente</TableCell>
                  <TableCell sx={{ color: '#a8a8a8', borderBottom: 'none', fontWeight: 600 }}>Estrellas</TableCell>
                  <TableCell sx={{ color: '#a8a8a8', borderBottom: 'none', fontWeight: 600 }}>Comentario</TableCell>
                  <TableCell sx={{ color: '#a8a8a8', borderBottom: 'none', fontWeight: 600 }}>Fecha</TableCell>
                  <TableCell sx={{ borderBottom: 'none' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {displayed.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: '#aaa' }}>
                      {tab === 0 ? 'No hay reseñas pendientes' : 'No hay reseñas aprobadas'}
                    </TableCell>
                  </TableRow>
                )}
                {displayed.map((item, i) => (
                  <TableRow key={i} hover>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {item.nurse_name} {item.nurse_lastname}
                    </TableCell>
                    <TableCell>{item.patient_name} {item.patient_lastname}</TableCell>
                    <TableCell>
                      <Rating
                        value={Number(item.rating_value)}
                        readOnly
                        size="small"
                        icon={<StarRateRoundedIcon fontSize="inherit" />}
                        emptyIcon={<StarRateRoundedIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: 280, color: '#555' }}>
                      {item.comment || <span style={{ color: '#ccc', fontStyle: 'italic' }}>Sin comentario</span>}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: '#888' }}>
                      {item.rating_date ? String(item.rating_date).slice(0, 10) : ''}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {!item.approved ? (
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<CheckCircleOutlineIcon />}
                          onClick={() => handleApprove(item.rating_id)}
                          sx={{ mr: 1 }}
                        >
                          Aprobar
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          color="warning"
                          startIcon={<CancelOutlinedIcon />}
                          onClick={() => handleReject(item.rating_id)}
                          sx={{ mr: 1 }}
                        >
                          Desaprobar
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlineOutlinedIcon />}
                        onClick={() => handleDelete(item.rating_id)}
                      >
                        Eliminar
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </div>

      <AlertDialog
        open={openAlert}
        setOpen={setOpenAlert}
        subtitle="¿Está seguro que desea eliminar esta reseña?"
        send={deleteItem}
      />
    </div>
  );
}
